import React, { useState, useEffect, useCallback } from 'react';
import { submittalApi, projectApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import {
  Plus, Funnel, Eye, Trash, PencilSimple, ChatCircle, Clock,
  CheckCircle, XCircle, WarningCircle, FilePdf, FileText, DownloadSimple,
  Package, ClipboardText, PaperPlane, UploadSimple, ChartPie,
  FileDashed, File, FileDoc, ArrowClockwise, Export, Notebook
} from '@phosphor-icons/react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  'Draft': { bg: '#F3F4F6', text: '#374151', fill: '#9CA3AF' },
  'Submitted': { bg: '#DBEAFE', text: '#1E40AF', fill: '#3B82F6' },
  'Under Review': { bg: '#FEF3C7', text: '#92400E', fill: '#F59E0B' },
  'Approved': { bg: '#D1FAE5', text: '#065F46', fill: '#10B981' },
  'Approved with Comments': { bg: '#A7F3D0', text: '#065F46', fill: '#34D399' },
  'Rejected': { bg: '#FEE2E2', text: '#991B1B', fill: '#EF4444' },
  'Revise & Resubmit': { bg: '#FED7AA', text: '#9A3412', fill: '#F97316' },
};
const PRIORITY_COLORS = { Low: '#6B7280', Medium: '#2563EB', High: '#F59E0B', Urgent: '#DC2626' };
const ORDER_COLORS = { 'Not Ordered': '#9CA3AF', 'Ordered': '#3B82F6', 'Partially Delivered': '#F59E0B', 'Delivered': '#10B981' };

const FILE_ICONS = {
  pdf: { icon: FilePdf, color: '#DC2626' },
  dwg: { icon: FileDashed, color: '#7C3AED' },
  dxf: { icon: FileDashed, color: '#7C3AED' },
  doc: { icon: FileDoc, color: '#2563EB' },
  docx: { icon: FileDoc, color: '#2563EB' },
  xls: { icon: FileText, color: '#059669' },
  xlsx: { icon: FileText, color: '#059669' },
  default: { icon: File, color: '#6B7280' },
};
const getFileIcon = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || FILE_ICONS.default;
};
const fmtSize = (b) => { if (!b) return ''; if (b < 1024) return `${b}B`; if (b < 1048576) return `${(b/1024).toFixed(1)}KB`; return `${(b/1048576).toFixed(1)}MB`; };

const MAIN_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: ChartPie },
  { key: 'submittals', label: 'Submittals', icon: ClipboardText },
  { key: 'templates', label: 'Form Templates', icon: Notebook },
];

export default function SubmittalsPage() {
  const [mainTab, setMainTab] = useState('dashboard');
  const [submittals, setSubmittals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterOrder, setFilterOrder] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubmittal, setSelectedSubmittal] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('details');

  useEffect(() => {
    Promise.all([projectApi.list(), submittalApi.types()])
      .then(([pRes, tRes]) => {
        setProjects(pRes.data);
        setTypes(tRes.data.types || []);
        setStatuses(tRes.data.statuses || []);
        setPriorities(tRes.data.priorities || []);
        setOrderStatuses(tRes.data.order_statuses || []);
      }).finally(() => setLoading(false));
  }, []);

  const loadSubmittals = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterType) params.type = filterType;
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    if (filterOrder) params.order_status = filterOrder;
    const [sRes, sumRes] = await Promise.all([
      submittalApi.list(params),
      submittalApi.summary({ project_id: filterProject || undefined }),
    ]);
    setSubmittals(sRes.data);
    setSummary(sumRes.data);
  }, [filterProject, filterType, filterStatus, filterPriority, filterOrder]);

  useEffect(() => { if (!loading) loadSubmittals(); }, [loadSubmittals, loading]);

  const openDetail = (sub) => { setSelectedSubmittal(sub); setActiveDetailTab('details'); setShowDetailModal(true); };

  // PDF Export
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(18); doc.setTextColor(26, 31, 54);
    doc.text('Technical Submittals Log', 14, 18);
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()} | Total: ${submittals.length} submittals`, 14, 25);

    const cols = ['No.', 'Title', 'Type', 'Priority', 'Status', 'Order Status', 'Supplier', 'Submitted To', 'Response Date', 'Action By'];
    const rows = submittals.map(s => [
      s.submittal_number, s.title, s.type, s.priority, s.status,
      s.procurement?.order_status || 'N/A', s.procurement?.supplier_name || '—',
      s.submitted_to_name || '—',
      s.required_response_date ? new Date(s.required_response_date).toLocaleDateString() : '—',
      s.action_required_by || '—',
    ]);
    autoTable(doc, {
      startY: 32, head: [cols], body: rows,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [26, 31, 54], textColor: 255, fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { bottom: 20 },
    });

    // Summary section at the end
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10); doc.setTextColor(26, 31, 54);
    doc.text('Summary', 14, finalY);
    doc.setFontSize(8); doc.setTextColor(80);
    if (summary) {
      let y = finalY + 6;
      doc.text(`Total: ${summary.total} | Overdue: ${summary.overdue}`, 14, y);
      y += 5;
      const statusLine = Object.entries(summary.by_status || {}).map(([k, v]) => `${k}: ${v}`).join(' | ');
      doc.text(`By Status: ${statusLine}`, 14, y);
      y += 5;
      const orderLine = Object.entries(summary.by_order_status || {}).map(([k, v]) => `${k}: ${v}`).join(' | ');
      doc.text(`By Order: ${orderLine}`, 14, y);
    }

    doc.setFontSize(7); doc.setTextColor(150);
    doc.text('ProjectBook Pro™ — Technical Submittals Report', 14, doc.internal.pageSize.height - 8);
    doc.save('Submittals_Log.pdf');
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="submittals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-heading)' }}>Technical Submittals</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Material submittals, shop drawings, approvals & procurement tracking</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportPDF}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-red-50"
            style={{ borderColor: 'var(--border)', color: '#DC2626' }}
            data-testid="export-pdf-btn">
            <Export className="w-4 h-4" /> Export PDF
          </button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-submittal-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Submittal
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }} data-testid="main-tabs">
        {MAIN_TABS.map(t => (
          <button key={t.key} onClick={() => setMainTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${mainTab === t.key ? 'border-[var(--primary)]' : 'border-transparent'}`}
            style={{ color: mainTab === t.key ? 'var(--primary)' : 'var(--text-muted)' }}
            data-testid={`main-tab-${t.key}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* DASHBOARD TAB */}
      {mainTab === 'dashboard' && summary && <DashboardView summary={summary} submittals={submittals} />}

      {/* SUBMITTALS TAB */}
      {mainTab === 'submittals' && (
        <SubmittalsListView
          submittals={submittals} projects={projects} types={types} statuses={statuses}
          priorities={priorities} orderStatuses={orderStatuses}
          filterProject={filterProject} setFilterProject={setFilterProject}
          filterType={filterType} setFilterType={setFilterType}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          filterPriority={filterPriority} setFilterPriority={setFilterPriority}
          filterOrder={filterOrder} setFilterOrder={setFilterOrder}
          showFilters={showFilters} setShowFilters={setShowFilters}
          onOpenDetail={openDetail}
        />
      )}

      {/* TEMPLATES TAB */}
      {mainTab === 'templates' && <TemplatesView />}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSubmittalModal
          projects={projects} types={types} priorities={priorities}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadSubmittals(); }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSubmittal && (
        <SubmittalDetailModal
          submittal={selectedSubmittal} statuses={statuses} orderStatuses={orderStatuses}
          activeTab={activeDetailTab} setActiveTab={setActiveDetailTab}
          onClose={() => { setShowDetailModal(false); setSelectedSubmittal(null); }}
          onUpdated={loadSubmittals}
          onRefresh={async () => { const r = await submittalApi.get(selectedSubmittal.id); setSelectedSubmittal(r.data); }}
        />
      )}
    </div>
  );
}

// ==================== DASHBOARD VIEW ====================
function DashboardView({ summary, submittals }) {
  const statusData = Object.entries(summary.by_status || {}).map(([name, value]) => ({
    name, value, fill: STATUS_COLORS[name]?.fill || '#9CA3AF',
  }));
  const typeData = Object.entries(summary.by_type || {}).map(([name, value]) => ({ name: name.length > 18 ? name.slice(0, 16) + '...' : name, value, fullName: name }));
  const orderData = Object.entries(summary.by_order_status || {}).map(([name, value]) => ({ name, value, fill: ORDER_COLORS[name] || '#9CA3AF' }));
  const priorityData = submittals.reduce((acc, s) => { acc[s.priority] = (acc[s.priority] || 0) + 1; return acc; }, {});
  const priorityChartData = Object.entries(priorityData).map(([name, value]) => ({ name, value, fill: PRIORITY_COLORS[name] || '#6B7280' }));

  const TYPE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4', '#D946EF'];

  return (
    <div className="space-y-4" data-testid="dashboard-view">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total" value={summary.total} color="#1A1F36" icon={ClipboardText} />
        <KpiCard label="Overdue" value={summary.overdue} color="#DC2626" icon={WarningCircle} />
        <KpiCard label="Approved" value={(summary.by_status?.['Approved'] || 0) + (summary.by_status?.['Approved with Comments'] || 0)} color="#10B981" icon={CheckCircle} />
        <KpiCard label="Under Review" value={summary.by_status?.['Under Review'] || 0} color="#F59E0B" icon={ArrowClockwise} />
        <KpiCard label="Rejected" value={summary.by_status?.['Rejected'] || 0} color="#EF4444" icon={XCircle} />
        <KpiCard label="Delivered" value={summary.by_order_status?.['Delivered'] || 0} color="#059669" icon={Package} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status Distribution - Donut */}
        <Card className="p-4" data-testid="chart-status">
          <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text-heading)' }}>Submittal Status Distribution</h3>
          {statusData.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={true} fontSize={10}>
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Count']} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Priority Breakdown - Donut */}
        <Card className="p-4" data-testid="chart-priority">
          <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text-heading)' }}>Priority Breakdown</h3>
          {priorityChartData.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={priorityChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} label={({ name, value }) => `${name}: ${value}`} labelLine={true} fontSize={11}>
                  {priorityChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Type Breakdown - Bar */}
        <Card className="p-4" data-testid="chart-type">
          <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text-heading)' }}>Submittals by Type</h3>
          {typeData.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={typeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {typeData.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Order Status - Bar */}
        <Card className="p-4" data-testid="chart-order">
          <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text-heading)' }}>Procurement Status</h3>
          {orderData.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={orderData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {orderData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Overdue List */}
      {summary.overdue > 0 && (
        <Card className="p-4 border-l-4 border-l-red-500" data-testid="overdue-list">
          <h3 className="text-sm font-bold mb-2 text-red-600 flex items-center gap-1.5">
            <WarningCircle className="w-4 h-4" weight="fill" /> Overdue Submittals ({summary.overdue})
          </h3>
          <div className="space-y-1">
            {submittals.filter(s => {
              if (!s.required_response_date) return false;
              return new Date(s.required_response_date) < new Date() && !['Approved', 'Approved with Comments'].includes(s.status);
            }).map(s => (
              <div key={s.id} className="flex items-center justify-between py-1.5 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold" style={{ color: 'var(--primary)' }}>{s.submittal_number}</span>
                  <span className="text-sm" style={{ color: 'var(--text-heading)' }}>{s.title}</span>
                </div>
                <span className="text-xs text-red-500 font-medium">Due: {new Date(s.required_response_date).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ label, value, color, icon: Icon }) {
  return (
    <Card className="p-3" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} weight="fill" />
        <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </Card>
  );
}

// ==================== SUBMITTALS LIST VIEW ====================
function SubmittalsListView({ submittals, projects, types, statuses, priorities, orderStatuses, filterProject, setFilterProject, filterType, setFilterType, filterStatus, setFilterStatus, filterPriority, setFilterPriority, filterOrder, setFilterOrder, showFilters, setShowFilters, onOpenDetail }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>{submittals.length} Submittal(s)</span>
        <button onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors"
          style={{ borderColor: 'var(--border)', color: showFilters ? 'var(--primary)' : 'var(--text-secondary)', backgroundColor: showFilters ? 'var(--primary-light, #EFF6FF)' : 'transparent' }}
          data-testid="toggle-filters-btn">
          <Funnel className="w-3.5 h-3.5" /> Filters
        </button>
      </div>

      {showFilters && (
        <Card className="p-3" data-testid="filters-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Select value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="filter-project"><option value="">All Projects</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
            <Select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="filter-type"><option value="">All Types</option>{types.map(t => <option key={t} value={t}>{t}</option>)}</Select>
            <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status"><option value="">All Statuses</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</Select>
            <Select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} data-testid="filter-priority"><option value="">All Priorities</option>{priorities.map(p => <option key={p} value={p}>{p}</option>)}</Select>
            <Select value={filterOrder} onChange={e => setFilterOrder(e.target.value)} data-testid="filter-order"><option value="">All Order Status</option>{orderStatuses.map(o => <option key={o} value={o}>{o}</option>)}</Select>
          </div>
        </Card>
      )}

      {submittals.length === 0 ? (
        <Card className="p-10 text-center" data-testid="no-submittals">
          <ClipboardText className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold" style={{ color: 'var(--text-heading)' }}>No submittals found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Create your first technical submittal to get started</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="submittals-table">
            <thead>
              <tr className="border-b-2" style={{ borderColor: 'var(--border)' }}>
                {['No.', 'Title', 'Type', 'Project', 'Priority', 'Status', 'Order', 'Due', 'Action By', ''].map(h => (
                  <th key={h} className="text-left pb-2.5 pr-3 text-[10px] font-bold uppercase whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {submittals.map(sub => {
                const isOverdue = sub.required_response_date && new Date(sub.required_response_date) < new Date() && !['Approved', 'Approved with Comments'].includes(sub.status);
                return (
                  <tr key={sub.id} className="border-b transition-colors hover:bg-[var(--hover-bg)] cursor-pointer" style={{ borderColor: 'var(--border)' }} onClick={() => onOpenDetail(sub)} data-testid={`submittal-row-${sub.id}`}>
                    <td className="py-2.5 pr-3 font-mono text-xs font-bold" style={{ color: 'var(--primary)' }}>{sub.submittal_number}</td>
                    <td className="py-2.5 pr-3">
                      <span className="font-medium text-sm" style={{ color: 'var(--text-heading)' }}>{sub.title}</span>
                      {sub.attachments?.length > 0 && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-muted)' }}>{sub.attachments.length} file(s)</span>}
                    </td>
                    <td className="py-2.5 pr-3"><span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-secondary)' }}>{sub.type}</span></td>
                    <td className="py-2.5 pr-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{sub.project_name || '—'}</td>
                    <td className="py-2.5 pr-3"><span className="text-xs font-bold" style={{ color: PRIORITY_COLORS[sub.priority] }}>{sub.priority}</span></td>
                    <td className="py-2.5 pr-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: STATUS_COLORS[sub.status]?.bg, color: STATUS_COLORS[sub.status]?.text }}>{sub.status}</span></td>
                    <td className="py-2.5 pr-3"><span className="text-xs font-medium" style={{ color: ORDER_COLORS[sub.procurement?.order_status] }}>{sub.procurement?.order_status || 'N/A'}</span></td>
                    <td className="py-2.5 pr-3 text-xs whitespace-nowrap" style={{ color: isOverdue ? '#DC2626' : 'var(--text-muted)' }}>
                      {sub.required_response_date ? new Date(sub.required_response_date).toLocaleDateString() : '—'}
                      {isOverdue && <WarningCircle className="w-3 h-3 inline ml-1" weight="fill" />}
                    </td>
                    <td className="py-2.5 pr-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{sub.action_required_by || '—'}</td>
                    <td className="py-2.5"><Eye className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== TEMPLATES VIEW ====================
function TemplatesView() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'General' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const CATEGORIES = ['General', 'Material Submittal', 'Shop Drawing', 'Method Statement', 'MEP', 'Structural', 'Architectural', 'Finishing'];

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await submittalApi.listTemplates(); setTemplates(r.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('category', form.category);
      if (file) formData.append('file', file);

      if (editId) {
        await submittalApi.updateTemplate(editId, formData);
      } else {
        if (!file) { setError('Please select a template file to upload'); setSaving(false); return; }
        await submittalApi.createTemplate(formData);
      }
      setShowUpload(false); setEditId(null); setForm({ name: '', description: '', category: 'General' }); setFile(null);
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (t) => {
    setEditId(t.id);
    setForm({ name: t.name, description: t.description || '', category: t.category || 'General' });
    setFile(null); setError('');
    setShowUpload(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await submittalApi.deleteTemplate(id); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const getTemplateFileUrl = (filename) => `${API_URL}/api/submittals/templates/file/${filename}`;

  if (loading) return <div className="flex justify-center py-6"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-3" data-testid="templates-view">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>{templates.length} Form Template(s)</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Upload branded submittal forms (PDF, DOCX, XLSX) for project-specific formatting</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm({ name: '', description: '', category: 'General' }); setFile(null); setError(''); setShowUpload(true); }}
          data-testid="upload-template-btn">
          <UploadSimple className="w-4 h-4 mr-1.5" /> Upload Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="p-10 text-center">
          <Notebook className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold" style={{ color: 'var(--text-heading)' }}>No form templates yet</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Upload your project-branded submittal forms to maintain consistent formatting</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map(t => {
            const fi = getFileIcon(t.original_filename);
            return (
              <Card key={t.id} className="p-4 flex flex-col" data-testid={`template-${t.id}`}>
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${fi.color}15` }}>
                    <fi.icon className="w-5 h-5" style={{ color: fi.color }} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-heading)' }}>{t.name}</p>
                    {t.description && <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{t.description}</p>}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-secondary)' }}>{t.category}</span>
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{t.original_filename} ({fmtSize(t.file_size)})</span>
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>By {t.uploaded_by_name} · {new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                  {t.filename && (
                    <>
                      <a href={getTemplateFileUrl(t.filename)} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors"
                        style={{ color: 'var(--primary)', backgroundColor: 'var(--primary-light, #EFF6FF)' }}
                        data-testid={`view-template-${t.id}`}>
                        <Eye className="w-3 h-3" /> View
                      </a>
                      <a href={getTemplateFileUrl(t.filename)} download={t.original_filename}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700"
                        data-testid={`download-template-${t.id}`}>
                        <DownloadSimple className="w-3 h-3" /> Download
                      </a>
                    </>
                  )}
                  <button onClick={() => handleEdit(t)} className="p-1 rounded transition-colors hover:bg-[var(--hover-bg)]" data-testid={`edit-template-${t.id}`}>
                    <PencilSimple className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                  </button>
                  <button onClick={() => handleDelete(t.id)} className="p-1 rounded transition-colors hover:bg-red-50" data-testid={`delete-template-${t.id}`}>
                    <Trash className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload/Edit Modal */}
      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title={editId ? 'Edit Template' : 'Upload Form Template'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="template-form">
          {error && <div className="p-2 bg-red-50 text-red-600 rounded text-sm">{error}</div>}
          <Input label="Template Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Material Submittal - Al Tayer Standard" data-testid="tpl-name" />
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
              rows={2} placeholder="Template details, when to use, etc." data-testid="tpl-description" />
          </div>
          <Select label="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="tpl-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>
              Template File {!editId && <span className="text-red-500">*</span>}
              {editId && <span className="text-[10px] font-normal ml-1">(leave empty to keep current file)</span>}
            </label>
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.ppt,.pptx" onChange={e => setFile(e.target.files[0])}
              className="text-sm" data-testid="tpl-file" />
            <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Accepted: PDF, DOCX, XLSX, DWG, DXF, PPTX</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-template-btn">{saving ? 'Saving...' : editId ? 'Update Template' : 'Upload Template'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ==================== CREATE MODAL ====================
function CreateSubmittalModal({ projects, types, priorities, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: '', type: 'Material Submittal', title: '', description: '',
    specification_section: '', priority: 'Medium', submitted_to_name: '',
    required_response_date: '', action_required_by: '', action_due_date: '',
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      files.forEach((f, i) => formData.append(`file${i}`, f));
      await submittalApi.create(formData);
      onCreated();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Create New Submittal">
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="create-submittal-form">
        {error && <div className="p-2 bg-red-50 text-red-600 rounded text-sm">{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Select label="Project" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })} required data-testid="sub-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="sub-type">
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </div>
        <Input label="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Structural Steel Grade 60" data-testid="sub-title" />
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Description</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
            rows={2} placeholder="Material specs, references, notes..." data-testid="sub-description" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Specification Section" value={form.specification_section} onChange={e => setForm({ ...form, specification_section: e.target.value })} placeholder="e.g. 03 20 00" data-testid="sub-spec" />
          <Select label="Priority" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="sub-priority">
            {priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Submit To (Reviewer)" value={form.submitted_to_name} onChange={e => setForm({ ...form, submitted_to_name: e.target.value })} placeholder="Consultant name" data-testid="sub-reviewer" />
          <Input label="Response Required By" type="date" value={form.required_response_date} onChange={e => setForm({ ...form, required_response_date: e.target.value })} data-testid="sub-response-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Action Required By" value={form.action_required_by} onChange={e => setForm({ ...form, action_required_by: e.target.value })} placeholder="Person name" data-testid="sub-action-by" />
          <Input label="Action Due Date" type="date" value={form.action_due_date} onChange={e => setForm({ ...form, action_due_date: e.target.value })} data-testid="sub-action-date" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Attachments (PDF, DWG, DXF, Images, AutoCAD)</label>
          <input type="file" multiple accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={e => setFiles(Array.from(e.target.files))} className="text-sm" data-testid="sub-files" />
          {files.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {files.map((f, i) => {
                const fi = getFileIcon(f.name);
                return (
                  <div key={i} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <fi.icon className="w-3.5 h-3.5" style={{ color: fi.color }} weight="fill" />
                    <span>{f.name}</span>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>({fmtSize(f.size)})</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-submittal-btn">{saving ? 'Creating...' : 'Create Submittal'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ==================== DETAIL MODAL ====================
function SubmittalDetailModal({ submittal, statuses, orderStatuses, activeTab, setActiveTab, onClose, onUpdated, onRefresh }) {
  const [sub, setSub] = useState(submittal);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [procForm, setProcForm] = useState(sub.procurement || {});
  const [procSaving, setProcSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', review_comments: '', rejection_reason: '' });

  useEffect(() => { setSub(submittal); setProcForm(submittal.procurement || {}); }, [submittal]);

  const loadComments = async () => { setCommentsLoading(true); try { const r = await submittalApi.getComments(sub.id); setComments(r.data); } catch {} finally { setCommentsLoading(false); } };
  const loadRevisions = async () => { setRevisionsLoading(true); try { const r = await submittalApi.getRevisions(sub.id); setRevisions(r.data); } catch {} finally { setRevisionsLoading(false); } };

  useEffect(() => {
    if (activeTab === 'comments') loadComments();
    if (activeTab === 'revisions') loadRevisions();
  }, [activeTab]);

  const handleEdit = () => {
    setEditForm({
      title: sub.title, description: sub.description, specification_section: sub.specification_section,
      priority: sub.priority, submitted_to_name: sub.submitted_to_name,
      required_response_date: sub.required_response_date ? sub.required_response_date.split('T')[0] : '',
      action_required_by: sub.action_required_by, action_due_date: sub.action_due_date ? sub.action_due_date.split('T')[0] : '',
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try { await submittalApi.update(sub.id, editForm); setEditing(false); await onRefresh(); onUpdated(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleStatusChange = async () => {
    setSaving(true);
    try { await submittalApi.changeStatus(sub.id, statusForm); setShowStatusModal(false); await onRefresh(); onUpdated(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleProcSave = async () => {
    setProcSaving(true);
    try { await submittalApi.updateProcurement(sub.id, procForm); await onRefresh(); onUpdated(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setProcSaving(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try { await submittalApi.addComment(sub.id, { comment: newComment.trim() }); setNewComment(''); loadComments(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this submittal permanently?')) return;
    try { await submittalApi.delete(sub.id); onClose(); onUpdated(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // Export single submittal PDF
  const exportDetailPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor(26, 31, 54);
    doc.text(`Submittal: ${sub.submittal_number}`, 14, 18);
    doc.setFontSize(10); doc.setTextColor(80);
    doc.text(sub.title, 14, 26);
    doc.setFontSize(8);

    const fields = [
      ['Type', sub.type], ['Status', sub.status], ['Priority', sub.priority],
      ['Spec Section', sub.specification_section || '—'],
      ['Submitted By', sub.submitted_by_name || '—'], ['Submit To', sub.submitted_to_name || '—'],
      ['Submission Date', sub.submission_date ? new Date(sub.submission_date).toLocaleDateString() : '—'],
      ['Response Date', sub.required_response_date ? new Date(sub.required_response_date).toLocaleDateString() : '—'],
      ['Action By', sub.action_required_by || '—'],
      ['Review Comments', sub.review_comments || '—'],
    ];

    autoTable(doc, { startY: 34, body: fields, theme: 'grid', styles: { fontSize: 8 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } } });

    // Procurement
    const proc = sub.procurement || {};
    if (proc.approved_product || proc.supplier_name || proc.order_status !== 'Not Ordered') {
      const procY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(12); doc.setTextColor(26, 31, 54);
      doc.text('Procurement Details', 14, procY);
      const procFields = [
        ['Approved Product', proc.approved_product || '—'], ['Brand', proc.approved_brand || '—'],
        ['Supplier', proc.supplier_name || '—'], ['Contact', proc.supplier_contact || '—'],
        ['Order Status', proc.order_status || '—'], ['PO Reference', proc.po_reference || '—'],
        ['Expected Delivery', proc.expected_delivery_date ? new Date(proc.expected_delivery_date).toLocaleDateString() : '—'],
        ['Actual Delivery', proc.actual_delivery_date ? new Date(proc.actual_delivery_date).toLocaleDateString() : '—'],
        ['Notes', proc.procurement_notes || '—'],
      ];
      autoTable(doc, { startY: procY + 4, body: procFields, theme: 'grid', styles: { fontSize: 8 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } } });
    }

    doc.setFontSize(7); doc.setTextColor(150);
    doc.text('ProjectBook Pro™ — Submittal Detail', 14, doc.internal.pageSize.height - 8);
    doc.save(`${sub.submittal_number}_Detail.pdf`);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const getFileUrl = (filename) => `${API_URL}/api/submittals/file/${filename}`;

  const TABS = [
    { key: 'details', label: 'Details', icon: ClipboardText },
    { key: 'procurement', label: 'Procurement', icon: Package },
    { key: 'comments', label: 'Comments', icon: ChatCircle },
    { key: 'revisions', label: 'Revisions', icon: Clock },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="submittal-detail-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] rounded-xl shadow-2xl border overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: 'var(--primary-light, #EFF6FF)', color: 'var(--primary)' }}>{sub.submittal_number}</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[sub.status]?.bg, color: STATUS_COLORS[sub.status]?.text }}>{sub.status}</span>
              <span className="text-xs font-bold" style={{ color: PRIORITY_COLORS[sub.priority] }}>{sub.priority}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={exportDetailPDF} className="px-2 py-1.5 text-xs font-medium rounded-md transition-colors hover:bg-red-50" style={{ color: '#DC2626' }} title="Export PDF" data-testid="export-detail-pdf">
                <Export className="w-4 h-4" />
              </button>
              <button onClick={() => { setStatusForm({ status: '', review_comments: '', rejection_reason: '' }); setShowStatusModal(true); }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-md text-white" style={{ backgroundColor: 'var(--primary)' }} data-testid="change-status-btn">Status</button>
              <button onClick={handleEdit} className="p-1.5 rounded hover:bg-[var(--hover-bg)]" data-testid="edit-submittal-btn"><PencilSimple className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
              <button onClick={handleDelete} className="p-1.5 rounded hover:bg-red-50" data-testid="delete-submittal-btn"><Trash className="w-4 h-4 text-red-500" /></button>
              <button onClick={onClose} className="px-2.5 py-1 text-xs font-medium rounded-md" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-heading)' }}>Close</button>
            </div>
          </div>
          <h2 className="text-lg font-bold mt-2" style={{ color: 'var(--text-heading)' }}>{sub.title}</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub.type} | {sub.specification_section || 'No spec'} | Rev {sub.current_revision}</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-4" style={{ borderColor: 'var(--border)' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors ${activeTab === t.key ? 'border-[var(--primary)]' : 'border-transparent'}`}
              style={{ color: activeTab === t.key ? 'var(--primary)' : 'var(--text-muted)' }} data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* DETAILS */}
          {activeTab === 'details' && !editing && (
            <div className="space-y-4" data-testid="details-tab">
              {sub.description && <InfoField label="Description" value={sub.description} />}
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Submitted By" value={sub.submitted_by_name} />
                <InfoField label="Submit To" value={sub.submitted_to_name} />
                <InfoField label="Submission Date" value={fmtDate(sub.submission_date)} />
                <InfoField label="Response Date" value={fmtDate(sub.required_response_date)} />
                <InfoField label="Action By" value={sub.action_required_by} />
                <InfoField label="Action Due Date" value={fmtDate(sub.action_due_date)} />
              </div>
              {sub.review_comments && <InfoField label="Review Comments" value={sub.review_comments} />}
              {sub.rejection_reason && <InfoField label="Rejection Reason" value={sub.rejection_reason} />}
              {/* Attachments */}
              {sub.attachments && sub.attachments.length > 0 && (
                <div>
                  <label className="text-[10px] font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Attachments ({sub.attachments.length})</label>
                  <div className="space-y-1.5 mt-1">
                    {sub.attachments.map((att, i) => {
                      const fi = getFileIcon(att.original_filename);
                      return (
                        <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
                          <div className="flex items-center gap-2.5">
                            <fi.icon className="w-5 h-5" style={{ color: fi.color }} weight="fill" />
                            <div>
                              <span className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>{att.original_filename}</span>
                              <span className="text-[10px] ml-2" style={{ color: 'var(--text-muted)' }}>{fmtSize(att.file_size)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <a href={getFileUrl(att.filename)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md" style={{ color: 'var(--primary)', backgroundColor: 'var(--primary-light, #EFF6FF)' }}
                              data-testid={`view-att-${i}`}><Eye className="w-3 h-3" /> View</a>
                            <a href={getFileUrl(att.filename)} download={att.original_filename}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700"
                              data-testid={`download-att-${i}`}><DownloadSimple className="w-3 h-3" /> Download</a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EDIT MODE */}
          {activeTab === 'details' && editing && (
            <div className="space-y-3" data-testid="edit-form">
              <Input label="Title" value={editForm.title || ''} onChange={e => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title" />
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Description</label>
                <textarea value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
                  rows={3} data-testid="edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Spec Section" value={editForm.specification_section || ''} onChange={e => setEditForm({ ...editForm, specification_section: e.target.value })} />
                <Input label="Submit To" value={editForm.submitted_to_name || ''} onChange={e => setEditForm({ ...editForm, submitted_to_name: e.target.value })} />
                <Input label="Response Date" type="date" value={editForm.required_response_date || ''} onChange={e => setEditForm({ ...editForm, required_response_date: e.target.value })} />
                <Input label="Action By" value={editForm.action_required_by || ''} onChange={e => setEditForm({ ...editForm, action_required_by: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </div>
          )}

          {/* PROCUREMENT */}
          {activeTab === 'procurement' && (
            <div className="space-y-4" data-testid="procurement-tab">
              <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--hover-bg)' }}>
                <p className="text-xs font-semibold uppercase mb-3 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}><Package className="w-3.5 h-3.5" /> Procurement & Delivery</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Approved Product" value={procForm.approved_product || ''} onChange={e => setProcForm({ ...procForm, approved_product: e.target.value })} data-testid="proc-product" />
                  <Input label="Brand" value={procForm.approved_brand || ''} onChange={e => setProcForm({ ...procForm, approved_brand: e.target.value })} data-testid="proc-brand" />
                  <Input label="Supplier" value={procForm.supplier_name || ''} onChange={e => setProcForm({ ...procForm, supplier_name: e.target.value })} data-testid="proc-supplier" />
                  <Input label="Contact" value={procForm.supplier_contact || ''} onChange={e => setProcForm({ ...procForm, supplier_contact: e.target.value })} data-testid="proc-contact" />
                  <Select label="Order Status" value={procForm.order_status || 'Not Ordered'} onChange={e => setProcForm({ ...procForm, order_status: e.target.value })} data-testid="proc-order-status">
                    {orderStatuses.map(o => <option key={o} value={o}>{o}</option>)}
                  </Select>
                  <Input label="PO Reference" value={procForm.po_reference || ''} onChange={e => setProcForm({ ...procForm, po_reference: e.target.value })} data-testid="proc-po" />
                  <Input label="Expected Delivery" type="date" value={procForm.expected_delivery_date ? procForm.expected_delivery_date.split('T')[0] : ''} onChange={e => setProcForm({ ...procForm, expected_delivery_date: e.target.value })} data-testid="proc-expected" />
                  <Input label="Actual Delivery" type="date" value={procForm.actual_delivery_date ? procForm.actual_delivery_date.split('T')[0] : ''} onChange={e => setProcForm({ ...procForm, actual_delivery_date: e.target.value })} data-testid="proc-actual" />
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Procurement Notes</label>
                  <textarea value={procForm.procurement_notes || ''} onChange={e => setProcForm({ ...procForm, procurement_notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
                    rows={2} placeholder="Delivery schedule, payment terms, etc." data-testid="proc-notes" />
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={handleProcSave} disabled={procSaving} data-testid="save-procurement-btn">{procSaving ? 'Saving...' : 'Save Procurement'}</Button>
                </div>
              </div>
            </div>
          )}

          {/* COMMENTS */}
          {activeTab === 'comments' && (
            <div className="space-y-3" data-testid="comments-tab">
              {commentsLoading ? <Spinner /> : (
                <>
                  {comments.length === 0 && <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>No comments yet</p>}
                  {comments.map(c => (
                    <div key={c.id} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border)' }} data-testid={`comment-${c.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold" style={{ color: 'var(--text-heading)' }}>{c.user_name}</span>
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{c.comment}</p>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                    <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..."
                      onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                      className="flex-1 px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
                      data-testid="comment-input" />
                    <Button onClick={handleAddComment} disabled={!newComment.trim()} data-testid="send-comment-btn"><PaperPlane className="w-4 h-4" /></Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* REVISIONS */}
          {activeTab === 'revisions' && (
            <div className="space-y-3" data-testid="revisions-tab">
              {revisionsLoading ? <Spinner /> : revisions.map(rev => (
                <div key={rev.id} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border)' }} data-testid={`revision-${rev.revision_number}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--primary-light, #EFF6FF)', color: 'var(--primary)' }}>Rev {rev.revision_number}</span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{rev.submitted_by_name}</span>
                    </div>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{new Date(rev.created_at).toLocaleString()}</span>
                  </div>
                  {rev.notes && <p className="text-sm mt-1" style={{ color: 'var(--text-heading)' }}>{rev.notes}</p>}
                  {rev.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {rev.attachments.map((att, i) => {
                        const fi = getFileIcon(att.original_filename);
                        return (
                          <a key={i} href={`${API_URL}/api/submittals/file/${att.filename}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs px-2 py-1 rounded border flex items-center gap-1" style={{ borderColor: 'var(--border)', color: fi.color }}>
                            <fi.icon className="w-3 h-3" weight="fill" /> {att.original_filename}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Change Modal */}
        {showStatusModal && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 rounded-xl">
            <div className="p-5 rounded-xl shadow-lg border w-80 space-y-3" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }} data-testid="status-change-modal">
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-heading)' }}>Change Status</h3>
              <Select label="New Status" value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="new-status-select">
                <option value="">-- Select --</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-heading)' }}>Review Comments</label>
                <textarea value={statusForm.review_comments} onChange={e => setStatusForm({ ...statusForm, review_comments: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-heading)' }}
                  rows={2} data-testid="status-comments" />
              </div>
              {(statusForm.status === 'Rejected' || statusForm.status === 'Revise & Resubmit') && (
                <Input label="Rejection Reason" value={statusForm.rejection_reason} onChange={e => setStatusForm({ ...statusForm, rejection_reason: e.target.value })} data-testid="status-rejection" />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                <Button onClick={handleStatusChange} disabled={!statusForm.status || saving} data-testid="confirm-status-btn">{saving ? '...' : 'Update'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>{label}</label>
      <p className="text-sm" style={{ color: 'var(--text-heading)' }}>{value || '—'}</p>
    </div>
  );
}
